import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "@/context/AuthContext";
import {
  Loader2, AlertTriangle, Wand2,
} from "lucide-react";
import { toast } from "sonner";
import { ESTADO_LABELS, ESTADO_CLS, fmtEur, fmtFechaOferta } from "./shared";

export default function MarketUniverse() {
  const [data, setData] = useState(null);
  const [municipio, setMunicipio] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [infering, setInfering] = useState(null);   // row id currently loading
  const [bulkRunning, setBulkRunning] = useState(false);
  const [minMm, setMinMm] = useState(0); // Iter284 · filtro de importe persistente
  const navigate = useNavigate();

  const load = () =>
    axios.get(`${API}/tender360/market-universe`)
      .then(r => setData(r.data))
      .catch((e) => toast.error("Market Universe: " + (e.response?.data?.detail || e.message)));

  useEffect(() => { load(); }, []);

  if (!data) return <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="animate-spin" size={14}/> Cargando…</div>;

  const { kpis, grid } = data;

  const inferOne = async (row) => {
    setInfering(row.id);
    try {
      const r = await axios.post(`${API}/tender360/m1/infer-budget/${row.id}`, {});
      toast.success(`≈ ${fmtEur(r.data.inferred_eur)} estimados (${r.data.meta.sector_label})`);
      await load();
    } catch (e) {
      const msg = e.response?.data?.detail?.detail || e.response?.data?.detail || e.message;
      toast.error("No se pudo estimar: " + msg);
    } finally {
      setInfering(null);
    }
  };

  const runBulk = async () => {
    if (!window.confirm(
      `Estimar presupuesto por CPV+años para todas las oportunidades sin importe declarado (${kpis.sin_importe_count} filas).\n\nSólo se procesan las que aún no tengan estimación previa.`
    )) return;
    setBulkRunning(true);
    try {
      const r = await axios.post(`${API}/tender360/m1/infer-budget-bulk`);
      toast.success(`${r.data.inferred_ok} estimadas · ${r.data.skipped} sin sector detectable (${r.data.processed} revisadas)`);
      await load();
    } catch (e) {
      toast.error("Bulk error: " + (e.response?.data?.detail || e.message));
    } finally {
      setBulkRunning(false);
    }
  };

  let filtered = municipio ? grid.filter(g => g.municipio === municipio) : grid;
  if (onlyMissing) filtered = filtered.filter(g => !g.has_importe);
  // Iter284 · filtro de importe (declarado o estimado) ≥ X M€
  if (minMm > 0) {
    filtered = filtered.filter(g => {
      const amount = g.has_importe ? (g.importe_licitacion || 0) : (g.importe_inferred_eur || 0);
      return amount >= minMm * 1_000_000;
    });
  }

  const kpiCards = [
    { label: "Licitaciones",           val: kpis.total_licitaciones },
    { label: "Ofertas TIC",            val: kpis.total_ofertas_tic },
    { label: "Mercado rastreado",      val: fmtEur(kpis.importe_mercado_rastreado_eur),
      sub: kpis.importe_estimado_eur > 0
        ? `${fmtEur(kpis.importe_declarado_eur)} declarado · ${fmtEur(kpis.importe_estimado_eur)} estimado`
        : null,
      testid: "kpi-mercado-rastreado" },
    { label: "CAPEX total ofertas",    val: fmtEur(kpis.capex_total_ofertas_eur) },
    { label: "OPEX total ofertas/año", val: fmtEur(kpis.opex_total_ofertas_eur) },
    { label: "Municipios únicos",      val: kpis.municipios_unicos },
  ];

  return (
    <div className="space-y-4" data-testid="market-universe">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {kpiCards.map(k => (
          <div key={k.label} className="bg-white rounded-xl border border-gray-200 p-3" data-testid={k.testid || undefined}>
            <div className="text-[10px] text-gray-500 uppercase font-semibold tracking-wide">{k.label}</div>
            <div className="text-lg font-bold text-gray-900 mt-1">{k.val}</div>
            {k.sub && <div className="text-[10px] text-gray-400 mt-0.5" title={k.sub}>{k.sub}</div>}
          </div>
        ))}
      </div>

      {/* Aviso masivo sin importe */}
      {kpis.sin_importe_count > 0 && (
        <div
          className="rounded-xl border border-amber-300 bg-amber-50/60 px-4 py-2.5 flex flex-wrap items-center gap-3 text-xs"
          data-testid="mu-sin-importe-banner">
          <AlertTriangle size={14} className="text-amber-600"/>
          <span className="text-amber-900 font-semibold">
            {kpis.sin_importe_count} licitaciones sin importe declarado
          </span>
          {kpis.sin_importe_inferido_count > 0 && (
            <span className="text-amber-700">
              · {kpis.sin_importe_inferido_count} ya estimadas por CPV+años
            </span>
          )}
          <span className="text-gray-500">
            Los KPIs muestran ceros porque PLACSP no expone el importe. Estimar por CPV+años
            (mediana histórica Valoriza).
          </span>
          <button
            onClick={runBulk}
            disabled={bulkRunning}
            data-testid="mu-bulk-infer-btn"
            className="ml-auto bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50">
            {bulkRunning ? <Loader2 size={12} className="animate-spin"/> : <Wand2 size={12}/>}
            Estimar todas
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={municipio}
          onChange={e => setMunicipio(e.target.value)}
          data-testid="market-municipio-filter"
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-xs bg-white"
        >
          <option value="">Todos los municipios</option>
          {data.municipios.map(m => <option key={m}>{m}</option>)}
        </select>
        <label className="inline-flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer border border-gray-200 rounded-lg px-2 py-1 bg-white">
          <input
            type="checkbox"
            checked={onlyMissing}
            onChange={e => setOnlyMissing(e.target.checked)}
            data-testid="mu-only-missing-toggle"
            className="accent-amber-500"/>
          Sólo sin importe declarado
        </label>
        {/* Iter284 · Importe ≥ X M€ — mismo patrón que Pipeline/YTD Board */}
        <div
          data-testid="mu-filter-importe-wrap"
          title="Filtra licitaciones con importe (declarado o estimado) por encima del umbral"
          className={`inline-flex items-center gap-1.5 border rounded-lg px-2 py-1 text-[11px] ${
            minMm > 0 ? "border-sky-300 bg-sky-50" : "border-gray-200 bg-white"
          }`}>
          <span className={minMm > 0 ? "font-semibold text-sky-700" : "text-gray-500"}>Importe ≥</span>
          <input
            type="range" min={0} max={30} step={1}
            value={minMm}
            onChange={(e) => setMinMm(Number(e.target.value))}
            data-testid="mu-filter-importe"
            className="accent-sky-500 w-20"
          />
          <span className={`font-mono w-12 text-right ${minMm > 0 ? "text-sky-700 font-bold" : "text-gray-500"}`}>{minMm}M€</span>
        </div>
        <span className="text-xs text-gray-500" data-testid="mu-count">
          {filtered.length} licitaciones
        </span>
      </div>

      {/* Grid */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-gray-600 uppercase text-[10px] tracking-wide">
            <tr>
              <th className="text-left p-2.5">Municipio</th>
              <th className="text-left p-2.5">Licitación</th>
              <th className="text-left p-2.5">Estado</th>
              <th className="text-right p-2.5">Importe</th>
              <th className="text-right p-2.5">CAPEX</th>
              <th className="text-right p-2.5">OPEX/año</th>
              <th className="text-center p-2.5">Prob.</th>
              <th className="text-left p-2.5">Presentación</th>
              <th className="text-left p-2.5">Responsable</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map(r => (
              <MarketUniverseRow
                key={r.id}
                row={r}
                onOpen={() => navigate(`/licitaciones/${r.id}`)}
                onInfer={() => inferOne(r)}
                infering={infering === r.id}
              />
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="py-10 text-center text-gray-400 text-xs">Sin licitaciones con ese filtro</div>}
      </div>
    </div>
  );
}

function MarketUniverseRow({ row, onOpen, onInfer, infering }) {
  const [hover, setHover] = useState(false);
  const importeCell = () => {
    // 1) Importe declarado real
    if (row.has_importe) {
      return <span className="font-mono">{fmtEur(row.importe_licitacion)}</span>;
    }
    // 2) Ya inferido → mostrar con badge "≈ estimado"
    if (row.importe_is_estimated && row.importe_inferred_eur > 0) {
      const meta = row.importe_inferred_meta || {};
      return (
        <span
          className="inline-flex items-center gap-1 justify-end w-full"
          title={`Estimado por CPV+años. Sector: ${meta.sector_label || "—"} · ${meta.years || "?"} años · ${meta.unit_per_year ? fmtEur(meta.unit_per_year) : "?"}/año`}
          data-testid={`mu-inferred-${row.id}`}>
          <span className="text-[9px] px-1 py-0.5 rounded bg-indigo-100 text-indigo-700 font-bold">≈</span>
          <span className="font-mono italic text-indigo-700">{fmtEur(row.importe_inferred_eur)}</span>
        </span>
      );
    }
    // 3) Sin importe → badge amarillo + botón Estimar
    return (
      <span className="inline-flex items-center gap-1.5 justify-end w-full">
        <span
          className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold"
          data-testid={`mu-sin-importe-${row.id}`}
          title="Sin importe declarado en el pliego">
          sin importe
        </span>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onInfer(); }}
          disabled={infering}
          data-testid={`mu-infer-btn-${row.id}`}
          title="Estimar por CPV+años (tabla sectorial Valoriza)"
          className="text-[10px] px-2 py-0.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white font-semibold flex items-center gap-1 disabled:opacity-50">
          {infering ? <Loader2 size={9} className="animate-spin"/> : <Wand2 size={9}/>}
          Estimar
        </button>
      </span>
    );
  };

  return (
    <tr
      onClick={onOpen}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={`hover:bg-gray-50 cursor-pointer ${row.importe_is_estimated ? "bg-indigo-50/30" : ""}`}
      data-testid={`grid-row-${row.id}`}>
      <td className="p-2.5 font-semibold text-gray-900">{row.municipio}</td>
      <td className="p-2.5 text-gray-700 max-w-xs truncate">{row.titulo}</td>
      <td className="p-2.5">
        <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${ESTADO_CLS[row.estado_crm] || "bg-gray-100 text-gray-600"}`}>
          {ESTADO_LABELS[row.estado_crm] || row.estado_crm}
        </span>
      </td>
      <td className="p-2.5 text-right">{importeCell()}</td>
      <td className="p-2.5 text-right font-mono text-gray-600">{fmtEur(row.capex_propuesto)}</td>
      <td className="p-2.5 text-right font-mono text-gray-600">{fmtEur(row.opex_propuesto)}</td>
      <td className="p-2.5 text-center">{row.probabilidad_exito}%</td>
      <td className="p-2.5 text-gray-500">{fmtFechaOferta(row.fecha_presentacion)}</td>
      <td className="p-2.5 text-gray-500">{row.responsable_comercial || "—"}</td>
    </tr>
  );
}
